import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { useState } from 'react';
import { Badge, Button, Callout, Field, SidePanel, Tabs, inputCls, textareaCls } from '../../components/ui';
import {
  type Decision,
  type Envelope,
  type Page,
  type Person,
  type Preview,
  type Registration,
  command,
  dateTime,
  directoryRequest,
} from '../../lib/personnel-api';
import { AccountSection } from './AccountSection';
import { ReviewPanel } from './ReviewPanel';
import {
  ErrorNotice,
  Loading,
  PanelSection,
  PreviewCommit,
  RegistrationFacts,
  StateBadge,
  useDirectoryCommand,
} from './parts';

export function PersonPanel({
  id,
  caps,
  onClose,
  onChanged,
}: { id: string; caps: string[]; onClose: () => void; onChanged: () => void }) {
  const qc = useQueryClient();
  const stableCommand = useDirectoryCommand();
  const [tab, setTab] = useState('profile');
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [basis, setBasis] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [reviewId, setReviewId] = useState<string | null>(null);
  const person = useQuery({
    queryKey: ['personnel', 'person', id],
    queryFn: () => directoryRequest<Person>(`/api/personnel/people/${encodeURIComponent(id)}`),
  });
  const history = useQuery({
    queryKey: ['personnel', 'person', id, 'history'],
    queryFn: () =>
      directoryRequest<Page<{ id: string; createdAtMs: number; revision: number; valueJson: string }>>(
        `/api/personnel/people/${encodeURIComponent(id)}/history`,
      ),
    enabled: tab === 'history',
  });
  const decisions = useQuery({
    queryKey: ['personnel', 'decisions', id],
    queryFn: () =>
      directoryRequest<Page<Decision>>(`/api/personnel/decisions?personId=${encodeURIComponent(id)}`),
    enabled: tab === 'history',
  });
  const p = person.data?.data;
  const allowed = p?.allowedActions ?? [];
  const changed = () => {
    onChanged();
    void qc.invalidateQueries({ queryKey: ['personnel', 'person', id] });
  };
  const save = async () => {
    if (!p) return;
    setBusy(true);
    setError(null);
    try {
      await directoryRequest(
        `/api/personnel/people/${encodeURIComponent(id)}`,
        'PATCH',
        stableCommand({ displayName: name.trim(), basis: basis.trim(), evidenceIds: [] }, [
          { kind: 'person', id, revision: p.profileRevision },
        ]),
      );
      setEditing(false);
      changed();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const manual = async () => {
    setError(null);
    try {
      const r = await directoryRequest<{ id: string }>('/api/personnel/reviews', 'POST', {
        origin: 'manual',
        personIds: [id],
        registrationIds: p?.registrations.map((r) => r.id) ?? [],
        returnContext: `/personnel?person=${encodeURIComponent(id)}`,
      });
      setReviewId(r.data.id);
    } catch (e) {
      setError(e);
    }
  };
  return (
    <>
      <SidePanel
        open
        label="Карточка человека"
        title={p?.displayName ?? 'Карточка человека'}
        onClose={onClose}
      >
        <div className="space-y-5">
          <ErrorNotice error={person.error} retry={() => void person.refetch()} />
          {person.isLoading && <Loading />}
          {p && (
            <>
              <div className="space-y-2">
                <StateBadge state={p.state} />
                <p className="break-all text-xs text-ink-3">Company ID · {p.id}</p>
                {p.canonicalId !== p.id && (
                  <p className="break-all text-xs text-ink-2">Основная карточка · {p.canonicalId}</p>
                )}
              </div>
              <Tabs
                label="Карточка человека"
                idPrefix="person-card"
                value={tab}
                onChange={setTab}
                items={[
                  { value: 'profile', label: 'Сведения' },
                  { value: 'sources', label: 'Регистрации' },
                  { value: 'history', label: 'История' },
                  ...(caps.includes('accounts.person_bind') || caps.includes('assignments.manage')
                    ? [{ value: 'access', label: 'Аккаунт и доступ' }]
                    : []),
                ]}
              />
              <div
                role="tabpanel"
                id={`person-card-panel-${tab}`}
                aria-labelledby={`person-card-tab-${tab}`}
                className="space-y-5"
              >
                {tab === 'profile' && (
                  <>
                    <PanelSection title="Выбранное имя">
                      <p className="text-sm font-medium">{p.displayName ?? 'Имя не получено'}</p>
                      <p className="text-sm text-ink-2">{p.basis ?? 'Выбрано из исходной регистрации'}</p>
                      {allowed.includes('personnel.update') && !editing && (
                        <Button
                          onClick={() => {
                            setName(p.displayName ?? '');
                            setBasis('');
                            setEditing(true);
                          }}
                        >
                          Изменить выбранные сведения
                        </Button>
                      )}
                      {editing && (
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            void save();
                          }}
                        >
                          <Field label="ФИО">
                            <input
                              aria-label="ФИО"
                              className={inputCls}
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                              required
                            />
                          </Field>
                          <Field label="Основание изменения">
                            <textarea
                              aria-label="Основание изменения"
                              className={textareaCls}
                              value={basis}
                              onChange={(e) => setBasis(e.target.value)}
                              required
                            />
                          </Field>
                          <div className="flex flex-wrap gap-2">
                            <Button onClick={() => setEditing(false)}>Отмена</Button>
                            <Button
                              variant="primary"
                              type="submit"
                              disabled={busy || !name.trim() || !basis.trim()}
                            >
                              Сохранить сведения
                            </Button>
                          </div>
                        </form>
                      )}
                    </PanelSection>
                    {p.memberships && (
                      <PanelSection title="Членство в бригадах">
                        {p.memberships.length ? (
                          p.memberships.map((m) => (
                            <div key={m.id} className="text-sm">
                              <p className="font-medium">{m.brigadeName}</p>
                              <p className="mt-1 text-xs text-ink-3">
                                {m.fromDate} — {m.toDateExclusive ?? 'без окончания'} (не включая)
                              </p>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-ink-2">Доступных записей о членстве нет.</p>
                        )}
                      </PanelSection>
                    )}
                    <Callout icon={Info}>
                      У каждого человека один постоянный Company ID. Отсутствие новых проходов не удаляет
                      карточку и подтверждённые связи.
                    </Callout>
                    {caps.includes('identity.correct') && (
                      <Button onClick={() => void manual()}>Открыть явный разбор связей</Button>
                    )}
                  </>
                )}
                {tab === 'sources' &&
                  (p.registrations.length === 0 ? (
                    <p className="text-sm text-ink-2">
                      Регистраций пока нет. Карточка создана до первого прохода.
                    </p>
                  ) : (
                    p.registrations.map((r) => <RegistrationDetail key={r.id} id={r.id} fallback={r} />)
                  ))}
                {tab === 'history' && (
                  <>
                    <ErrorNotice error={history.error ?? decisions.error} />
                    {history.isLoading && <Loading />}
                    <PanelSection title="Версии карточки">
                      {history.data?.data.items.map((h) => (
                        <div key={h.id} className="border-b border-line pb-3 text-sm">
                          <p>Редакция {h.revision}</p>
                          <p className="text-xs text-ink-3">{dateTime(h.createdAtMs)}</p>
                        </div>
                      ))}
                      {!history.data?.data.items.length && (
                        <p className="text-sm text-ink-2">Изменений карточки пока нет.</p>
                      )}
                    </PanelSection>
                    <PanelSection title="Решения по идентичности">
                      {decisions.data?.data.items.map((d) => (
                        <DecisionRow
                          key={d.id}
                          decision={d}
                          canUndo={caps.includes('identity.correct')}
                          onChanged={changed}
                        />
                      ))}
                      {!decisions.data?.data.items.length && (
                        <p className="text-sm text-ink-2">Решений по идентичности пока нет.</p>
                      )}
                    </PanelSection>
                  </>
                )}
                {tab === 'access' && (
                  <AccountSection
                    personId={id}
                    personDisplayName={p.displayName}
                    caps={caps}
                    onChanged={changed}
                  />
                )}
              </div>
            </>
          )}
          <ErrorNotice error={error} retry={() => void person.refetch()} />
        </div>
      </SidePanel>
      {reviewId && (
        <ReviewPanel
          id={reviewId}
          manual
          canCorrect={caps.includes('identity.correct')}
          personIds={[id]}
          registrations={p?.registrations}
          onClose={() => setReviewId(null)}
          onChanged={changed}
        />
      )}
    </>
  );
}
export function RegistrationDetail({ id, fallback }: { id: string; fallback: Registration }) {
  const r = useQuery({
    queryKey: ['personnel', 'registration', id],
    queryFn: () => directoryRequest<Registration>(`/api/personnel/registrations/${encodeURIComponent(id)}`),
  });
  return (
    <section className="space-y-3 rounded-card border border-line p-4">
      <RegistrationFacts registration={r.data?.data ?? fallback} />
      <ErrorNotice error={r.error} />
      {r.data?.data.aliases?.length ? (
        <details>
          <summary className="cursor-pointer text-sm text-accent">Подтверждённые алиасы и периоды</summary>
          <div className="mt-2 space-y-2">
            {r.data.data.aliases.map((a) => (
              <div key={a.id} className="rounded-control bg-subtle p-3 text-xs">
                <p className="break-all">Company ID · {a.personId}</p>
                <p className="mt-1">
                  {a.fromMs == null ? 'Без ограничения начала' : dateTime(a.fromMs)} —{' '}
                  {a.toMs == null ? 'без ограничения конца' : dateTime(a.toMs)} (не включая)
                </p>
              </div>
            ))}
          </div>
        </details>
      ) : (
        <p className="text-xs text-ink-3">Подтверждённых алиасов нет.</p>
      )}
    </section>
  );
}
function DecisionRow({
  decision: d,
  canUndo,
  onChanged,
}: { decision: Decision; canUndo: boolean; onChanged: () => void }) {
  const [error, setError] = useState<unknown>(null);
  const [preview, setPreview] = useState<Envelope<Preview> | null>(null);
  const [basis, setBasis] = useState('');
  const [undo, setUndo] = useState(false);
  const actionLabels: Record<string, string> = {
    same_person: 'Связаны регистрации',
    different_people: 'Разные люди',
    new_person: 'Создана отдельная карточка',
    defer: 'Решение отложено',
    correct: 'Исправлена связь',
    split: 'Разделены связи',
    merge_persons: 'Объединены карточки',
    undo: 'Отменено решение',
  };
  return (
    <div className="space-y-2 border-b border-line pb-3">
      <p className="text-sm font-medium">
        {d.origin === 'automatic'
          ? `Автоматическое сопоставление${d.policyVersion ? ` · правило ${d.policyVersion}` : ''}`
          : (actionLabels[d.action] ?? d.action)}
      </p>
      <p className="text-xs text-ink-3">{dateTime(d.createdAtMs)}</p>
      <p className="text-sm text-ink-2">
        {d.origin === 'automatic'
          ? 'Однозначное совпадение ФИО; регистрация связана с карточкой человека по правилам справочника.'
          : (d.basis ?? d.input?.basis)}
      </p>
      {canUndo && (
        <Button size="sm" onClick={() => setUndo(!undo)}>
          Отменить решение…
        </Button>
      )}
      {undo && (
        <>
          <Field label="Основание отмены">
            <textarea
              aria-label="Основание отмены"
              className={textareaCls}
              value={basis}
              onChange={(e) => setBasis(e.target.value)}
            />
          </Field>
          <Button
            disabled={!basis.trim()}
            onClick={async () => {
              try {
                setPreview(
                  await directoryRequest<Preview>(
                    `/api/personnel/decisions/${encodeURIComponent(d.id)}/undo/preview`,
                    'POST',
                    { basis: basis.trim() },
                  ),
                );
              } catch (e) {
                setError(e);
              }
            }}
          >
            Проверить последствия отмены
          </Button>
        </>
      )}
      <ErrorNotice error={error} />
      {preview && (
        <PreviewCommit
          title="Отменить решение?"
          preview={preview}
          input={{ basis: basis.trim() }}
          endpoint={`/api/personnel/decisions/${encodeURIComponent(d.id)}/undo`}
          onCancel={() => setPreview(null)}
          onDone={() => {
            setPreview(null);
            setUndo(false);
            onChanged();
          }}
        />
      )}
    </div>
  );
}
